import { ThemeEvents, CartUpdateEvent } from '@theme/events';

const OPT_OUT_KEY = 'inner-self:package-protection-opt-out';

class PackageProtectionManager extends HTMLElement {
  connectedCallback() {
    if (document.querySelector('package-protection-manager') !== this) return;

    this.abortController = new AbortController();
    const options = { signal: this.abortController.signal };
    document.addEventListener('change', this.onChange, options);
    document.addEventListener('click', this.onClick, options);
    document.addEventListener('pointerover', this.onPointerOver, options);
    document.addEventListener('pointerout', this.onPointerOut, options);
    document.addEventListener('pointermove', this.onPointerMove, options);
    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate, options);
    this.syncUi();

    requestAnimationFrame(() => this.reconcile());
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.clearHoverClose();
  }

  get variantId() {
    return Number(this.dataset.variantId);
  }

  get isCanada() {
    return this.dataset.country === 'CA';
  }

  get hasProtection() {
    return Number(this.dataset.protectionQuantity || 0) > 0;
  }

  get hasMerchandise() {
    return Number(this.dataset.merchandiseCount || 0) > 0;
  }

  get optedOut() {
    try {
      return localStorage.getItem(OPT_OUT_KEY) === 'true';
    } catch {
      return false;
    }
  }

  setOptedOut(value) {
    try {
      if (value) localStorage.setItem(OPT_OUT_KEY, 'true');
      else localStorage.removeItem(OPT_OUT_KEY);
    } catch {
      // The cart still works when browser storage is unavailable.
    }
  }

  onCartUpdate = (event) => {
    if (event.detail?.data?.source === 'package-protection-manager') return;

    const cart = event.detail?.resource;
    if (cart?.items) {
      this.updateFromCart(cart);
      this.syncUi();
      requestAnimationFrame(() => this.reconcile());
      return;
    }

    this.fetchCart()
      .then((freshCart) => {
        this.updateFromCart(freshCart);
        this.syncUi();
        requestAnimationFrame(() => this.reconcile());
      })
      .catch(() => {
        // The next cart render will reconnect the manager and retry.
      });
  };

  onChange = (event) => {
    const input = event.target.closest?.('[data-package-protection-toggle]');
    if (!(input instanceof HTMLInputElement)) return;

    if (input.checked) {
      this.setOptedOut(false);
      if (!this.hasProtection) this.addProtection();
    } else {
      this.setOptedOut(true);
      if (this.hasProtection) this.removeProtection();
    }
  };

  onPointerOver = (event) => {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    this.pointerX = event.clientX;
    this.pointerY = event.clientY;
    const openButton = event.target.closest?.('[data-package-protection-open]');
    if (!(openButton instanceof HTMLButtonElement)) return;
    if (event.relatedTarget instanceof Node && openButton.contains(event.relatedTarget)) return;

    this.openDialog(openButton);
  };

  onPointerOut = (event) => {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    this.pointerX = event.clientX;
    this.pointerY = event.clientY;
    const openButton = event.target.closest?.('[data-package-protection-open]');
    if (!(openButton instanceof HTMLButtonElement)) return;
    if (event.relatedTarget instanceof Node && openButton.contains(event.relatedTarget)) return;

    this.scheduleHoverClose(openButton);
  };

  onPointerMove = (event) => {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    this.pointerX = event.clientX;
    this.pointerY = event.clientY;
    const dialog = document.querySelector('[data-package-protection-dialog][open]');
    if (!(dialog instanceof HTMLDialogElement)) return;

    const button = document.querySelector(
      `[data-package-protection-open][aria-controls="${CSS.escape(dialog.id)}"]`
    );
    if (!(button instanceof HTMLButtonElement)) return;

    if (this.pointerIsInside(button) || this.pointerIsInside(dialog)) {
      this.clearHoverClose();
    } else if (!this.hoverCloseTimer) {
      this.scheduleHoverClose(button);
    }
  };

  onClick = (event) => {
    const openButton = event.target.closest?.('[data-package-protection-open]');
    if (openButton instanceof HTMLButtonElement) {
      this.openDialog(openButton);
      return;
    }

    const closeButton = event.target.closest?.('[data-package-protection-close]');
    if (closeButton instanceof HTMLButtonElement) {
      const dialog = closeButton.closest('dialog');
      if (dialog instanceof HTMLDialogElement) {
        dialog.close();
        document.querySelectorAll('[data-package-protection-open]').forEach((button) => {
          button.setAttribute('aria-expanded', 'false');
        });
      }
    }
  };

  openDialog(button) {
    const dialog = document.getElementById(button.getAttribute('aria-controls') || '');
    if (!(dialog instanceof HTMLDialogElement)) return;

    this.clearHoverClose();
    button.setAttribute('aria-expanded', 'true');
    if (!dialog.open) dialog.showModal();
    this.positionDialog(dialog, button);
  }

  scheduleHoverClose(button) {
    this.clearHoverClose();
    const dialog = document.getElementById(button.getAttribute('aria-controls') || '');

    this.hoverCloseTimer = window.setTimeout(() => {
      if (!(dialog instanceof HTMLDialogElement)) return;
      this.hoverCloseTimer = undefined;
      if (this.pointerIsInside(button) || this.pointerIsInside(dialog)) return;
      this.closeDialog(dialog);
    }, 180);
  }

  pointerIsInside(element) {
    if (!Number.isFinite(this.pointerX) || !Number.isFinite(this.pointerY)) return false;
    const rect = element.getBoundingClientRect();
    return (
      this.pointerX >= rect.left &&
      this.pointerX <= rect.right &&
      this.pointerY >= rect.top &&
      this.pointerY <= rect.bottom
    );
  }

  clearHoverClose() {
    if (!this.hoverCloseTimer) return;
    window.clearTimeout(this.hoverCloseTimer);
    this.hoverCloseTimer = undefined;
  }

  closeDialog(dialog) {
    this.clearHoverClose();
    if (dialog.open) dialog.close();
    document.querySelectorAll('[data-package-protection-open]').forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
    });
  }

  positionDialog(dialog, button) {
    const buttonRect = button.getBoundingClientRect();
    const viewportMargin = 12;
    const anchorX = buttonRect.left + buttonRect.width / 2;
    const anchorY = buttonRect.top + buttonRect.height / 2;
    const dialogWidth = dialog.offsetWidth;
    const dialogHeight = dialog.offsetHeight;

    const left = Math.max(
      viewportMargin,
      Math.min(window.innerWidth - dialogWidth - viewportMargin, anchorX - dialogWidth)
    );
    const top = Math.max(
      viewportMargin,
      Math.min(window.innerHeight - dialogHeight - viewportMargin, anchorY - dialogHeight)
    );

    dialog.style.left = `${left}px`;
    dialog.style.top = `${top}px`;
    dialog.style.setProperty(
      '--package-protection-origin-x',
      `${Math.max(0, Math.min(dialogWidth, anchorX - left))}px`
    );
    dialog.style.setProperty(
      '--package-protection-origin-y',
      `${Math.max(0, Math.min(dialogHeight, anchorY - top))}px`
    );
  }

  syncUi() {
    const shouldBeChecked =
      this.isCanada && this.hasMerchandise && (this.hasProtection || !this.optedOut);

    document.querySelectorAll('[data-package-protection-toggle]').forEach((input) => {
      if (input instanceof HTMLInputElement) input.checked = shouldBeChecked;
    });

    document.querySelectorAll('[data-package-protection-dialog]').forEach((dialog) => {
      if (dialog.dataset.packageProtectionBound === 'true') return;
      dialog.dataset.packageProtectionBound = 'true';
      dialog.addEventListener('close', () => {
        document.querySelectorAll('[data-package-protection-open]').forEach((button) => {
          button.setAttribute('aria-expanded', 'false');
        });
      });
      dialog.addEventListener('pointerenter', (event) => {
        if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
          this.pointerX = event.clientX;
          this.pointerY = event.clientY;
          this.clearHoverClose();
        }
      });
      dialog.addEventListener('pointerleave', (event) => {
        if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
          this.pointerX = event.clientX;
          this.pointerY = event.clientY;
          const button = document.querySelector(
            `[data-package-protection-open][aria-controls="${CSS.escape(dialog.id)}"]`
          );
          if (button instanceof HTMLButtonElement) this.scheduleHoverClose(button);
        }
      });
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) this.closeDialog(dialog);
      });
    });
  }

  async reconcile() {
    if (!this.hasMerchandise) {
      this.setOptedOut(false);
      if (this.hasProtection) await this.removeProtection();
      return;
    }

    if (!this.isCanada) {
      if (this.hasProtection) await this.removeProtection();
      return;
    }

    if (this.optedOut) {
      if (this.hasProtection) await this.removeProtection();
      return;
    }

    if (!this.hasProtection) {
      await this.addProtection();
    }
  }

  sectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((component) => component.dataset.sectionId)
      .filter(Boolean)
      .filter((id, index, ids) => ids.indexOf(id) === index);
  }

  requestBody(extra) {
    const sectionIds = this.sectionIds();
    return {
      ...extra,
      ...(sectionIds.length ? { sections: sectionIds.join(',') } : {}),
      sections_url: window.location.pathname,
    };
  }

  async addProtection() {
    if (
      this.busy ||
      window.__innerSelfPackageProtectionBusy ||
      !this.isCanada ||
      !this.hasMerchandise ||
      this.hasProtection
    ) return;
    this.busy = true;
    window.__innerSelfPackageProtectionBusy = true;
    this.setBusy(true, 'Adding protection…');

    try {
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(
          this.requestBody({
            items: [{
              id: this.variantId,
              quantity: 1,
              properties: { _package_protection: 'true' },
            }],
          })
        ),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to add protection.');
      await this.refresh(result.sections);
    } catch (error) {
      this.showError(error instanceof Error ? error.message : 'Unable to add protection.');
      this.dataset.protectionQuantity = '0';
      this.syncUi();
    } finally {
      this.busy = false;
      window.__innerSelfPackageProtectionBusy = false;
      this.setBusy(false);
    }
  }

  async removeProtection() {
    if (this.busy || window.__innerSelfPackageProtectionBusy || !this.hasProtection) return;
    this.busy = true;
    window.__innerSelfPackageProtectionBusy = true;
    this.setBusy(true, 'Removing protection…');

    try {
      const cart = await this.fetchCart();
      const protectionLines = cart.items.filter((item) => Number(item.variant_id) === this.variantId);
      let sections;

      for (const [index, item] of protectionLines.entries()) {
        const response = await fetch(window.Theme?.routes?.cart_change_url || '/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(this.requestBody({ id: item.key, quantity: 0 })),
        });
        const result = await response.json();
        if (!response.ok || result.status || result.errors) {
          throw new Error(result.description || result.message || result.errors || 'Unable to remove protection.');
        }
        if (index === protectionLines.length - 1) sections = result.sections;
      }

      await this.refresh(sections);
    } catch (error) {
      this.showError(error instanceof Error ? error.message : 'Unable to remove protection.');
      this.syncUi();
    } finally {
      this.busy = false;
      window.__innerSelfPackageProtectionBusy = false;
      this.setBusy(false);
    }
  }

  async fetchCart() {
    const response = await fetch(window.Theme?.routes?.cart_url ? `${window.Theme.routes.cart_url}.js` : '/cart.js', {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Unable to refresh the cart.');
    return response.json();
  }

  updateFromCart(cart) {
    const protectionItems = cart.items.filter((item) => Number(item.variant_id) === this.variantId);
    const protectionQuantity = protectionItems.reduce((total, item) => total + item.quantity, 0);
    const merchandiseCount = cart.item_count - protectionQuantity;

    this.dataset.protectionQuantity = String(protectionQuantity);
    this.dataset.protectionLineKey = protectionItems[0]?.key || '';
    this.dataset.merchandiseCount = String(merchandiseCount);
  }

  async refresh(sections) {
    const cart = await this.fetchCart();
    this.updateFromCart(cart);
    const merchandiseCount = Number(this.dataset.merchandiseCount || 0);
    this.syncUi();

    document.dispatchEvent(
      new CartUpdateEvent(cart, 'package-protection-manager', {
        source: 'package-protection-manager',
        itemCount: merchandiseCount,
        variantId: String(this.variantId),
        sections,
      })
    );
  }

  setBusy(isBusy, message = '') {
    document.querySelectorAll('[data-package-protection-ui]').forEach((ui) => {
      ui.toggleAttribute('aria-busy', isBusy);
    });
    document.querySelectorAll('[data-package-protection-toggle]').forEach((input) => {
      if (input instanceof HTMLInputElement) input.disabled = isBusy;
    });
    if (message) this.showStatus(message);
    else this.showStatus('');
  }

  showStatus(message) {
    document.querySelectorAll('[data-package-protection-status]').forEach((status) => {
      status.textContent = message;
    });
  }

  showError(message) {
    this.showStatus(message);
  }
}

if (!customElements.get('package-protection-manager')) {
  customElements.define('package-protection-manager', PackageProtectionManager);
}
